import { useGetPlanEntitlement } from './usePlanEntitlement.js';
import { NewResourceType } from '../../resources/components/NewResource/config.js';
import { toast } from '../../store/alerts.js';
import { showPlanUpgradePrompt } from '../utils/showPlanUpgradePrompt.js';
import { getPlans } from '../pricing.config.js';

const GUARD_REASON_KEY_MESSAGE_MAP = {
  maxFileSizeMB: "Some items are too large",
  maxTotalStorageSize: "You have run out of storage",
  credits: "You have run out of thinking credits",
  voiceNote: "Upgrade your plan to enable voice notes",
  integrationsEnabled: "Upgrade your plan to enable connections",
  bulkImportEnabled: "Upgrade your plan to enable bulk import",
  recapsEnabled: "Upgrade your plan to enable recaps",
  modelSwitchingEnabled: "Unlock all models & higher limits",
  audioTranscriptionEnabled: "Upgrade your plan to enable audio transcription"
};
const entitlementNotAvailableGuardReturnValue = {
  check: (_, options) => {
    if (options?.onAllowed) {
      options.onAllowed();
    }
    return {
      isAllowed: true
    };
  }
};
const useFeatureGuard_upload = () => {
  const { getPlanEntitlement } = useGetPlanEntitlement();
  if (!getPlanEntitlement) {
    return entitlementNotAvailableGuardReturnValue;
  }
  const value = {
    check: (files, options) => {
      const totalSize = files.reduce((acc, file) => acc + file.size, 0);
      const largestFileSize = Math.max(...files.map((file) => file.size));
      if (!getPlanEntitlement("maxFileSizeMB").isAllowed(largestFileSize)) {
        const nextPlan = Object.entries(getPlans()).find(([_, plan]) => {
          if (!plan.purchaseable) {
            return false;
          }
          const nextFeatureValue = plan.entitlements.maxFileSizeMB;
          return nextFeatureValue * 1e3 * 1e3 > largestFileSize;
        });
        const showUpsellBanner = () => {
          if (!nextPlan) {
            toast({
              content: files.length > 1 ? `One or more files are too big to upload` : `This file is too big to upload`
            });
            return;
          }
          showPlanUpgradePrompt({
            planEntitlement: "maxFileSizeMB",
            tier: nextPlan[0]
          });
        };
        if (options?.showUpsellBannerOnNotAllowed) {
          showUpsellBanner();
        }
        return {
          isAllowed: false,
          reason: "maxFileSizeMB",
          showUpsellBanner
        };
      }
      if (!getPlanEntitlement("maxTotalStorageSize").isAllowed(totalSize)) {
        const showUpsellBanner = () => showPlanUpgradePrompt({
          planEntitlement: "maxTotalStorageSizeMB"
        });
        if (options?.showUpsellBannerOnNotAllowed) {
          showUpsellBanner();
        }
        return {
          isAllowed: false,
          reason: "maxTotalStorageSize",
          showUpsellBanner
        };
      }
      if (!getPlanEntitlement("credits").isAllowed((costs) => {
        return costs.RESOURCE_CREATED * files.length;
      })) {
        const showUpsellBanner = () => {
          showPlanUpgradePrompt({
            planEntitlement: "creditCentsMonthly"
          });
        };
        if (options?.showUpsellBannerOnNotAllowed) {
          showUpsellBanner();
        }
        return {
          isAllowed: false,
          reason: "credits",
          showUpsellBanner
        };
      }
      return {
        isAllowed: true
      };
    }
  };
  return value;
};
const useFeatureGuard_createResources = () => {
  const { getPlanEntitlement } = useGetPlanEntitlement();
  if (!getPlanEntitlement) {
    return entitlementNotAvailableGuardReturnValue;
  }
  const value = {
    check: (types, options) => {
      const nonFolderTypes = types.filter((t) => t !== NewResourceType.Folder);
      if (nonFolderTypes.includes(NewResourceType.VoiceNote)) {
        if (!getPlanEntitlement("voiceNote").isAllowed()) {
          const showUpsellBanner = () => {
            showPlanUpgradePrompt({
              planEntitlement: "aiVoiceAndMeetingRecordingEnabled"
            });
          };
          if (options?.showUpsellBannerOnNotAllowed) {
            showUpsellBanner();
          }
          return {
            isAllowed: false,
            reason: "voiceNote",
            showUpsellBanner
          };
        }
      }
      if (!getPlanEntitlement("credits").isAllowed((costs) => {
        return costs.RESOURCE_CREATED * nonFolderTypes.length;
      })) {
        const showUpsellBanner = () => {
          showPlanUpgradePrompt({
            planEntitlement: "creditCentsMonthly"
          });
        };
        if (options?.showUpsellBannerOnNotAllowed) {
          showUpsellBanner();
        }
        return {
          isAllowed: false,
          reason: "credits",
          showUpsellBanner
        };
      }
      if (options?.onAllowed) {
        options.onAllowed();
      }
      return {
        isAllowed: true
      };
    }
  };
  return value;
};
const useFeatureGuard_uploadFilesAndCreateResources = () => {
  const featureGuardUpload = useFeatureGuard_upload();
  const featureGuardCreateResource = useFeatureGuard_createResources();
  return {
    check: (files, options) => {
      const resultUpload = featureGuardUpload.check(files, options);
      if (!resultUpload.isAllowed) {
        return resultUpload;
      }
      return featureGuardCreateResource.check(files.map(() => NewResourceType.Upload), options);
    }
  };
};
const useFeatureGuard_installIntegration = () => {
  const { getPlanEntitlement } = useGetPlanEntitlement();
  if (!getPlanEntitlement) {
    return entitlementNotAvailableGuardReturnValue;
  }
  const value = {
    check: (_, options) => {
      if (!getPlanEntitlement("integrationsEnabled").isAllowed()) {
        const showUpsellBanner = () => {
          showPlanUpgradePrompt({
            planEntitlement: "integrationsEnabled"
          });
        };
        if (options?.showUpsellBannerOnNotAllowed) {
          showUpsellBanner();
        }
        return {
          isAllowed: false,
          reason: "integrationsEnabled",
          showUpsellBanner
        };
      }
      if (options?.onAllowed) {
        options.onAllowed();
      }
      return {
        isAllowed: true
      };
    }
  };
  return value;
};
const useFeatureGuard_integrationFileSync = () => {
  const { getPlanEntitlement } = useGetPlanEntitlement();
  if (!getPlanEntitlement) {
    return entitlementNotAvailableGuardReturnValue;
  }
  const value = {
    check: (_, options) => {
      if (!getPlanEntitlement("integrationsEnabled").isAllowed()) {
        const showUpsellBanner = () => {
          showPlanUpgradePrompt({
            planEntitlement: "integrationsEnabled"
          });
        };
        if (options?.showUpsellBannerOnNotAllowed) {
          showUpsellBanner();
        }
        return {
          isAllowed: false,
          reason: "integrationsEnabled",
          showUpsellBanner
        };
      }
      if (!getPlanEntitlement("maxTotalStorageSize").isAllowed(2e8)) {
        const showUpsellBanner = () => {
          showPlanUpgradePrompt({
            planEntitlement: "maxTotalStorageSizeMB"
          });
        };
        if (options?.showUpsellBannerOnNotAllowed) {
          showUpsellBanner();
        }
        return {
          isAllowed: false,
          reason: "maxTotalStorageSize",
          showUpsellBanner
        };
      }
      if (options?.onAllowed) {
        options.onAllowed();
      }
      return {
        isAllowed: true
      };
    }
  };
  return value;
};
const useFeatureGuard_import = () => {
  const { getPlanEntitlement } = useGetPlanEntitlement();
  if (!getPlanEntitlement) {
    return entitlementNotAvailableGuardReturnValue;
  }
  const value = {
    check: (file, options) => {
      if (!getPlanEntitlement("bulkImportEnabled").isAllowed()) {
        const showUpsellBanner = () => {
          showPlanUpgradePrompt({
            planEntitlement: "bulkImportEnabled"
          });
        };
        if (options?.showUpsellBannerOnNotAllowed) {
          showUpsellBanner();
        }
        return {
          isAllowed: false,
          reason: "bulkImportEnabled",
          showUpsellBanner
        };
      }
      if (file) {
        if (!getPlanEntitlement("maxTotalStorageSize").isAllowed(file.size * 1.5)) {
          const showUpsellBanner = () => showPlanUpgradePrompt({
            planEntitlement: "maxTotalStorageSizeMB"
          });
          if (options?.showUpsellBannerOnNotAllowed) {
            showUpsellBanner();
          }
          return {
            isAllowed: false,
            reason: "maxTotalStorageSize",
            showUpsellBanner
          };
        }
      }
      if (options?.onAllowed) {
        options.onAllowed();
      }
      return {
        isAllowed: true
      };
    }
  };
  return value;
};
const useFeatureGuard_recaps = () => {
  const { getPlanEntitlement } = useGetPlanEntitlement();
  if (!getPlanEntitlement) {
    return entitlementNotAvailableGuardReturnValue;
  }
  const value = {
    check: (_, options) => {
      if (!getPlanEntitlement("recapsEnabled").isAllowed()) {
        const showUpsellBanner = () => {
          showPlanUpgradePrompt({
            planEntitlement: "recapsEnabled"
          });
        };
        if (options?.showUpsellBannerOnNotAllowed) {
          showUpsellBanner();
        }
        return {
          isAllowed: false,
          reason: "recapsEnabled",
          showUpsellBanner
        };
      }
      if (options?.onAllowed) {
        options.onAllowed();
      }
      return {
        isAllowed: true
      };
    }
  };
  return value;
};
const useFeatureGuard_modelSwitching = () => {
  const { getPlanEntitlement } = useGetPlanEntitlement();
  if (!getPlanEntitlement) {
    return entitlementNotAvailableGuardReturnValue;
  }
  const value = {
    check: (_, options) => {
      if (!getPlanEntitlement("modelSwitchingEnabled").isAllowed()) {
        const showUpsellBanner = () => {
          showPlanUpgradePrompt({
            planEntitlement: "modelSwitchingEnabled"
          });
        };
        if (options?.showUpsellBannerOnNotAllowed) {
          showUpsellBanner();
        }
        return {
          isAllowed: false,
          reason: "modelSwitchingEnabled",
          showUpsellBanner
        };
      }
      if (options?.onAllowed) {
        options.onAllowed();
      }
      return {
        isAllowed: true
      };
    }
  };
  return value;
};
const useFeatureGuard_audioTranscription = () => {
  const { getPlanEntitlement } = useGetPlanEntitlement();
  if (!getPlanEntitlement) {
    return entitlementNotAvailableGuardReturnValue;
  }
  const value = {
    check: (_, options) => {
      if (!getPlanEntitlement("audioTranscriptionEnabled").isAllowed()) {
        const showUpsellBanner = () => {
          showPlanUpgradePrompt({
            planEntitlement: "audioTranscriptionEnabled"
          });
        };
        if (options?.showUpsellBannerOnNotAllowed) {
          showUpsellBanner();
        }
        return {
          isAllowed: false,
          reason: "audioTranscriptionEnabled",
          showUpsellBanner
        };
      }
      if (options?.onAllowed) {
        options.onAllowed();
      }
      return {
        isAllowed: true
      };
    }
  };
  return value;
};

export { GUARD_REASON_KEY_MESSAGE_MAP, useFeatureGuard_audioTranscription, useFeatureGuard_createResources, useFeatureGuard_import, useFeatureGuard_installIntegration, useFeatureGuard_integrationFileSync, useFeatureGuard_modelSwitching, useFeatureGuard_recaps, useFeatureGuard_upload, useFeatureGuard_uploadFilesAndCreateResources };
//# sourceMappingURL=useFeatureGuard.js.map
